<template>
  <div id="branchCatalogSyncPage" class="p-4">
    <vs-row vs-type="flex" vs-w="12" class="mb-4">
      <vs-col vs-w="12">
        <p class="text-sm text-grey">
          Kaynak şubedeki stok kartları, reçete/ekstra maliyetleri ve fiyat listesi yapılandırmasını
          hedef şubeye aktarır. Eksik stok kayıtları oluşturularak sipariş sırasında
          "Stok Bilgisi Bulunamadı" hatası giderilir.
        </p>
      </vs-col>
    </vs-row>

    <vs-card>
      <vs-row vs-type="flex" vs-w="12">
        <vs-col vs-w="6" class="p-2">
          <label class="text-sm font-semibold">Kaynak Şube</label>
          <v-select
            v-model="form.sourceBranch"
            :options="branchOptions"
            label="name"
            itemid="id"
            placeholder="Kaynak şube seçin"
            :clearable="false"
          />
        </vs-col>
        <vs-col vs-w="6" class="p-2">
          <label class="text-sm font-semibold">Hedef Şube</label>
          <v-select
            v-model="form.targetBranch"
            :options="branchOptions"
            label="name"
            itemid="id"
            placeholder="Hedef şube seçin"
            :clearable="false"
          />
        </vs-col>
      </vs-row>

      <vs-row vs-type="flex" vs-w="12" class="mt-2">
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.syncStocks">Stok kalemlerini kopyala</vs-checkbox>
        </vs-col>
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.copyStockQuantities" :disabled="!form.syncStocks">
            Stok miktarlarını kopyala
          </vs-checkbox>
        </vs-col>
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.overwriteExisting">Mevcut kayıtların üzerine yaz</vs-checkbox>
        </vs-col>
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.syncRecipeBuyPrices">Reçete maliyetlerini aktar</vs-checkbox>
        </vs-col>
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.syncHalfMadeBuyPrices">Yarı mamul maliyetlerini aktar</vs-checkbox>
        </vs-col>
        <vs-col vs-w="4" class="p-2">
          <vs-checkbox v-model="form.syncExtraBuyPrices">Ekstra maliyetlerini aktar</vs-checkbox>
        </vs-col>
      </vs-row>

      <vs-row vs-type="flex" vs-w="12" class="mt-2">
        <vs-col vs-w="6" class="p-2">
          <label class="text-sm font-semibold">Fiyat Listesi Stratejisi</label>
          <v-select
            v-model="form.priceListStrategy"
            :options="priceListStrategyOptions"
            label="label"
            itemid="value"
            :reduce="o => o.value"
            :clearable="false"
          />
        </vs-col>
        <vs-col vs-w="6" class="p-2" v-if="form.priceListStrategy === 3">
          <label class="text-sm font-semibold">Yeni Fiyat Listesi Adı</label>
          <vs-input v-model="form.newPriceListName" class="w-full" placeholder="Örn: Şube X Fiyat Listesi" />
        </vs-col>
      </vs-row>

      <vs-row vs-type="flex" vs-w="12" class="mt-4" vs-justify="flex-end">
        <vs-button color="primary" type="border" class="mr-2" :disabled="loading" @click="runPreview">
          Önizle
        </vs-button>
        <vs-button color="success" :disabled="loading || !preview" @click="confirmSyncPopup = true">
          Aktar
        </vs-button>
      </vs-row>
    </vs-card>

    <vs-card v-if="preview" class="mt-4">
      <h4 class="mb-2">Önizleme: {{ preview.sourceBranchName }} → {{ preview.targetBranchName }}</h4>

      <div
        v-for="section in previewSections"
        :key="section.key"
        class="preview-section mb-4"
      >
        <h5 class="mb-1">{{ section.title }}</h5>
        <p class="text-sm mb-2">
          Eklenecek: <strong>{{ section.data.created }}</strong> ·
          Güncellenecek: <strong>{{ section.data.updated }}</strong> ·
          Atlanacak: <strong>{{ section.data.skipped }}</strong>
        </p>

        <template v-if="section.data.toCreate.length">
          <p class="text-sm font-semibold text-success mb-1">
            Eklenecek kayıtlar ({{ section.data.toCreate.length }})
          </p>
          <vs-table :data="section.data.toCreate" pagination max-items="15" class="mb-3">
            <template slot="thead">
              <vs-th>ID</vs-th>
              <vs-th v-if="section.showCode">Stok Kodu</vs-th>
              <vs-th>Ad</vs-th>
              <vs-th>Detay</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="(row, idx) in data" :key="'create-' + section.key + '-' + idx">
                <vs-td>{{ row.id }}</vs-td>
                <vs-td v-if="section.showCode">{{ row.code || '—' }}</vs-td>
                <vs-td>{{ row.name }}</vs-td>
                <vs-td>{{ row.info }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </template>

        <template v-if="section.data.toUpdate.length">
          <p class="text-sm font-semibold text-warning mb-1">
            Güncellenecek kayıtlar ({{ section.data.toUpdate.length }})
          </p>
          <vs-table :data="section.data.toUpdate" pagination max-items="15" class="mb-3">
            <template slot="thead">
              <vs-th>ID</vs-th>
              <vs-th v-if="section.showCode">Stok Kodu</vs-th>
              <vs-th>Ad</vs-th>
              <vs-th>Detay</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="(row, idx) in data" :key="'update-' + section.key + '-' + idx">
                <vs-td>{{ row.id }}</vs-td>
                <vs-td v-if="section.showCode">{{ row.code || '—' }}</vs-td>
                <vs-td>{{ row.name }}</vs-td>
                <vs-td>{{ row.info }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </template>

        <p
          v-if="!section.data.toCreate.length && !section.data.toUpdate.length && section.data.created === 0 && section.data.updated === 0"
          class="text-sm text-grey"
        >
          Bu bölümde eksik veya güncellenecek kayıt yok.
        </p>
      </div>

      <vs-row v-if="preview.priceListSharedWarning" vs-type="flex" vs-w="12" class="mt-2">
        <vs-col vs-w="12">
          <vs-alert color="warning" active="true">
            Hedef fiyat listesi ({{ preview.targetPriceListName }}) {{ preview.sharedPriceListBranchCount }} şube
            tarafından paylaşılıyor: {{ preview.sharedPriceListBranchNames.join(', ') }}.
            "Mevcut listeyi güncelle" seçeneği tüm bu şubeleri etkiler. Güvenli seçenek: yeni liste oluştur.
          </vs-alert>
        </vs-col>
      </vs-row>
    </vs-card>

    <vs-card class="mt-4">
      <h4 class="mb-2">İşlem Geçmişi</h4>
      <vs-table :data="historyItems" pagination max-items="10">
        <template slot="thead">
          <vs-th>Tarih</vs-th>
          <vs-th>Kaynak</vs-th>
          <vs-th>Hedef</vs-th>
          <vs-th>Kullanıcı</vs-th>
          <vs-th>Durum</vs-th>
          <vs-th>Detay</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr v-for="(tr, indextr) in data" :key="indextr">
            <vs-td>{{ formatDate(tr.created) }}</vs-td>
            <vs-td>{{ tr.sourceBranchName }}</vs-td>
            <vs-td>{{ tr.targetBranchName }}</vs-td>
            <vs-td>{{ tr.userName }}</vs-td>
            <vs-td>{{ statusLabel(tr.status) }}</vs-td>
            <vs-td>
              <vs-button size="small" type="border" @click="openHistoryDetail(tr)">Görüntüle</vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vs-card>

    <vs-popup title="Aktarım Onayı" :active.sync="confirmSyncPopup">
      <p>
        <strong>{{ form.sourceBranch && form.sourceBranch.name }}</strong> şubesinden
        <strong>{{ form.targetBranch && form.targetBranch.name }}</strong> şubesine katalog aktarımı yapılacak.
        Devam etmek istiyor musunuz?
      </p>
      <vs-row vs-type="flex" vs-w="12" class="mt-4" vs-justify="space-between">
        <vs-button color="primary" type="border" @click="confirmSyncPopup = false">Vazgeç</vs-button>
        <vs-button color="success" :disabled="loading" @click="runSync">Onayla ve Aktar</vs-button>
      </vs-row>
    </vs-popup>

    <vs-popup title="İşlem Detayı" :active.sync="historyDetailPopup">
      <pre v-if="selectedHistory" class="text-sm">{{ historyDetailText }}</pre>
    </vs-popup>
  </div>
</template>

<script>
import {
  getBranchNameList2,
  previewCatalogSync,
  syncCatalogFromBranch,
  getCatalogSyncHistory
} from "@/api/Branch";

export default {
  name: "BranchCatalogSync",
  data() {
    return {
      loading: false,
      branchOptions: [],
      preview: null,
      historyItems: [],
      confirmSyncPopup: false,
      historyDetailPopup: false,
      selectedHistory: null,
      form: {
        sourceBranch: null,
        targetBranch: null,
        syncStocks: true,
        copyStockQuantities: false,
        overwriteExisting: false,
        syncRecipeBuyPrices: true,
        syncHalfMadeBuyPrices: true,
        syncExtraBuyPrices: true,
        priceListStrategy: 0,
        newPriceListName: ""
      },
      priceListStrategyOptions: [
        { value: 0, label: "Fiyat listesine dokunma" },
        { value: 1, label: "Kaynak şubenin fiyat listesini ata" },
        { value: 2, label: "Hedef listenin fiyatlarını kaynakla eşitle" },
        { value: 3, label: "Kaynaktan yeni fiyat listesi oluştur (önerilen)" }
      ]
    };
  },
  computed: {
    historyDetailText() {
      if (!this.selectedHistory) return "";
      return JSON.stringify(
        {
          options: this.tryParseJson(this.selectedHistory.optionsJson),
          result: this.tryParseJson(this.selectedHistory.resultJson),
          error: this.selectedHistory.errorMessage
        },
        null,
        2
      );
    },
    previewSections() {
      if (!this.preview) return [];
      const p = this.preview;
      const sections = [];
      if (this.form.syncStocks) {
        sections.push({
          key: "stocks",
          title: "Stok Kartları",
          data: this.normalizeSection(p.stocks),
          showCode: true
        });
      }
      if (this.form.syncRecipeBuyPrices) {
        sections.push({
          key: "recipeBuyPrices",
          title: "Reçete Maliyetleri",
          data: this.normalizeSection(p.recipeBuyPrices),
          showCode: false
        });
      }
      if (this.form.syncHalfMadeBuyPrices) {
        sections.push({
          key: "halfMadeBuyPrices",
          title: "Yarı Mamul Maliyetleri",
          data: this.normalizeSection(p.halfMadeBuyPrices),
          showCode: false
        });
      }
      if (this.form.syncExtraBuyPrices) {
        sections.push({
          key: "extraBuyPrices",
          title: "Ekstra Maliyetleri",
          data: this.normalizeSection(p.extraBuyPrices),
          showCode: false
        });
      }
      if (this.form.priceListStrategy === 1 || this.form.priceListStrategy === 2) {
        sections.push(
          {
            key: "productPrices",
            title: "Ürün Fiyatları",
            data: this.normalizeSection(p.productPrices),
            showCode: false
          },
          {
            key: "extraProductPrices",
            title: "Ekstra Ürün Fiyatları",
            data: this.normalizeSection(p.extraProductPrices),
            showCode: false
          }
        );
      }
      return sections;
    }
  },
  mounted() {
    this.loadBranches();
    this.loadHistory();
  },
  methods: {
    loadBranches() {
      getBranchNameList2().then(list => {
        this.branchOptions = list || [];
      });
    },
    loadHistory() {
      getCatalogSyncHistory({ pageIndex: 0, pageSize: 20 }).then(res => {
        if (res && res.status === 200) {
          this.historyItems = res.data.items || res.data.Items || [];
        }
      });
    },
    buildPayload() {
      return {
        sourceBranchId: this.form.sourceBranch.id,
        targetBranchId: this.form.targetBranch.id,
        syncStocks: this.form.syncStocks,
        copyStockQuantities: this.form.copyStockQuantities,
        overwriteExisting: this.form.overwriteExisting,
        syncRecipeBuyPrices: this.form.syncRecipeBuyPrices,
        syncHalfMadeBuyPrices: this.form.syncHalfMadeBuyPrices,
        syncExtraBuyPrices: this.form.syncExtraBuyPrices,
        priceListStrategy: this.form.priceListStrategy,
        newPriceListName: this.form.newPriceListName
      };
    },
    validateForm() {
      if (!this.form.sourceBranch || !this.form.targetBranch) {
        this.$vs.notify({ title: "Uyarı", text: "Kaynak ve hedef şube seçilmelidir.", color: "warning" });
        return false;
      }
      if (this.form.sourceBranch.id === this.form.targetBranch.id) {
        this.$vs.notify({ title: "Uyarı", text: "Kaynak ve hedef şube aynı olamaz.", color: "warning" });
        return false;
      }
      if (this.form.priceListStrategy === 3 && !this.form.newPriceListName) {
        this.$vs.notify({ title: "Uyarı", text: "Yeni fiyat listesi adı girilmelidir.", color: "warning" });
        return false;
      }
      return true;
    },
    runPreview() {
      if (!this.validateForm()) return;
      this.loading = true;
      previewCatalogSync(this.buildPayload())
        .then(res => {
          if (res && res.status === 200) {
            this.preview = res.data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    runSync() {
      if (!this.validateForm()) return;
      this.loading = true;
      syncCatalogFromBranch(this.buildPayload())
        .then(res => {
          if (res && res.status === 200) {
            this.preview = res.data;
            this.confirmSyncPopup = false;
            this.$vs.notify({
              title: "Başarılı",
              text: res.data.message || "Katalog aktarımı tamamlandı.",
              color: "success"
            });
            this.loadHistory();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openHistoryDetail(row) {
      this.selectedHistory = row;
      this.historyDetailPopup = true;
    },
    tryParseJson(value) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("tr-TR");
    },
    statusLabel(status) {
      if (status === 1) return "Başarılı";
      if (status === 2) return "Başarısız";
      if (status === 3) return "Kısmi";
      return status;
    },
    normalizeSection(section) {
      if (!section) {
        return { created: 0, updated: 0, skipped: 0, toCreate: [], toUpdate: [] };
      }
      const pick = (camel, pascal, fallback) => {
        if (section[camel] != null) return section[camel];
        if (section[pascal] != null) return section[pascal];
        return fallback;
      };
      return {
        created: pick("created", "Created", 0),
        updated: pick("updated", "Updated", 0),
        skipped: pick("skipped", "Skipped", 0),
        toCreate: pick("toCreate", "ToCreate", []).map(this.normalizeItem),
        toUpdate: pick("toUpdate", "ToUpdate", []).map(this.normalizeItem)
      };
    },
    normalizeItem(item) {
      const pick = (camel, pascal) => {
        if (item[camel] != null) return item[camel];
        return item[pascal];
      };
      return {
        id: pick("id", "Id"),
        code: pick("code", "Code"),
        name: pick("name", "Name"),
        info: pick("info", "Info")
      };
    }
  }
};
</script>
